import React from 'react';
import moment from 'moment-timezone';
import { Text, View, StyleSheet } from 'react-native';
import { withTranslation } from 'react-i18next';
import { calcTotalMinutes, getTotalMinutesCeil } from 'fitbeat.gym-core';
import { getBodyImage } from '../../utils/bodyGroupImage';
import Theme from '../../theme';
import { Tile } from '../../ui/components';
import { defaultScale } from '../../utils/scaling';
import { getConnectedAppIcon } from '../../ConnectedApps/ui';
import { getActivityTypeIcon } from '../../utils/activityIconMapping';

const widthAndHeight = defaultScale(35);

interface IProps {
  gender?: string;
  workoutHistory: any;
  t: (key: string) => string;
  openWorkoutHistory: (workoutHistory: any) => void;
}

interface IState {
  overlappedByWorkoutId: any;
  formattedStartDayMonth: string;
  formattedStartTime: string;
  formattedWorkoutDuration: string;
  bodyGroupIcon: React.ReactNode;
  importOrigin: string;
  activityType: any;
}

const style = {
  connectedAppIcon: {
    shadowOffset: { width: 0, height: 1 },
    shadowColor: Theme.BLACK,
    shadowRadius: 2,
    borderRadius: 5,
    shadowOpacity: 0.1,
  },
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: Theme.WHITE,
    paddingVertical: defaultScale(5),
    marginHorizontal: defaultScale(10),
  },
  greyedOut: {
    opacity: 0.4,
  },
  cell: {
    flex: 1,
    flexBasis: 0,
    paddingVertical: defaultScale(10),
    borderRightWidth: defaultScale(1),
    alignItems: 'center',
    justifyContent: 'center',
    borderRightColor: Theme.GREY_LIGHT,
  },
  largerCell: {
    flex: 1.6,
  },
  noBorder: {
    borderRightWidth: 0,
  },
  iconContainer: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: -defaultScale(5),
  },
  overlayIcon: {
    position: 'absolute',
    bottom: -defaultScale(12),
    right: -defaultScale(6),
  },
  date: {
    ...Theme.graphStyle.customMedium14,
    color: Theme.GREY_DARK,
    fontFamily: Theme.CustomBold,
    textAlign: 'center',
  },
  bodyGroupImage: {
    width: defaultScale(35),
    height: defaultScale(35),
  },
  workoutType: {
    ...Theme.graphStyle.customMedium14,
    color: Theme.GREY_DARK,
    textAlign: 'center',
    fontFamily: Theme.CustomBold,
  },
  metric: {
    ...Theme.graphStyle.customMedium14,
    color: Theme.GREY_DARK,
    textAlign: 'center',
    fontFamily: Theme.CustomBold,
  },
});

export class WorkoutHistoryRow extends React.Component<IProps, IState> {
  public static defaultProps = {
    gender: 'female',
  };

  public static formatDuration(workoutTotalMinutes) {
    const workoutHours = Math.floor(workoutTotalMinutes / 60);
    const remainingMinutes = workoutTotalMinutes % 60;
    const workoutDuration = workoutHours > 0
      ? `${workoutHours}h ${remainingMinutes}m`
      : `${workoutTotalMinutes} min`;
    return workoutDuration;
  }

  constructor(props: IProps) {
    super(props);
    const { gender = 'female', workoutHistory, t } = props;
    const {
      settings: { activityType, cardioOrResistance, threeBodyGroups },
    } = workoutHistory;
    const { importOrigin } = workoutHistory;
    const { overlappedByWorkoutId } = workoutHistory;
    const startToShow = workoutHistory.startTimeOfFirstExercise
      ? workoutHistory.startTimeOfFirstExercise
      : workoutHistory.startTime;
    const totalMinutes = calcTotalMinutes(workoutHistory);
    const workoutMinutes = getTotalMinutesCeil(workoutHistory.workoutType, totalMinutes);
    const formattedWorkoutDuration = WorkoutHistoryRow.formatDuration(workoutMinutes);

    this.state = {
      overlappedByWorkoutId,
      formattedStartDayMonth: moment(startToShow).format('D/M/YY'),
      formattedStartTime: moment(startToShow).format('HH:mm'),
      formattedWorkoutDuration,
      bodyGroupIcon: getBodyImage(threeBodyGroups, gender, cardioOrResistance, widthAndHeight, widthAndHeight),
      importOrigin: importOrigin?.origin ?? 'Fitillion',
      activityType,
    };
  }

  public shouldComponentUpdate() {
    return false;
  }

  public openWorkoutHistory = () => {
    const { openWorkoutHistory, workoutHistory } = this.props;
    openWorkoutHistory(workoutHistory);
  };

  public render() {
    const { workoutHistory } = this.props;
    let { performanceMetrics } = workoutHistory;
    const {
      formattedWorkoutDuration,
      overlappedByWorkoutId,
      formattedStartDayMonth,
      formattedStartTime,
      activityType,
      importOrigin,
      bodyGroupIcon,
    } = this.state;
    if (!performanceMetrics) {
      performanceMetrics = {
        load: '-',
        calories: '-',
        averageHeartRate: '-',
      };
    }

    return (
      <Tile
        onPress={this.openWorkoutHistory}
        customStyle={[
          styles.container,
          overlappedByWorkoutId && styles.greyedOut,
        ]}
      >
        <View style={[styles.cell, styles.largerCell]}>
          <Text style={styles.date}>{formattedStartDayMonth}</Text>
          <Text style={styles.date}>{formattedStartTime}</Text>
        </View>
        <View style={[styles.cell, styles.largerCell]}>
          <Text style={styles.workoutType}>{formattedWorkoutDuration}</Text>
        </View>
        <View style={styles.cell}>
          <View style={styles.iconContainer}>
            {getActivityTypeIcon(activityType)}
            <View style={styles.overlayIcon}>
              {getConnectedAppIcon(importOrigin as any, defaultScale(15), style.connectedAppIcon)}
            </View>
          </View>
        </View>
        <View style={styles.cell}>
          <Text style={styles.metric}>{performanceMetrics.load}</Text>
        </View>
        <View style={styles.cell}>
          <Text style={styles.metric}>{performanceMetrics.calories}</Text>
        </View>
        <View style={styles.cell}>
          <Text style={styles.metric}>{performanceMetrics.averageHeartRate}</Text>
        </View>
        <View style={[styles.cell, styles.noBorder]}>
          {bodyGroupIcon}
        </View>
      </Tile>
    );
  }
}

export default withTranslation(['workoutHistory'])(WorkoutHistoryRow);
